import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";
import { getParkSelector } from "./Redux/appStateSlice";
import { parkNameWithoutOperator } from "./utils";

const ERROR_PAGE = {
  page_name: "error page",
  page_section: "error page",
  page_type: "error page",
  error_type: "error",
  error_name: "page not found",
};

const STATIC_PAGES = {
  "/": { page_name: "homepage", page_section: "homepage", page_type: "homepage" },
  "/404": ERROR_PAGE,
  "/parks": { page_name: "browse parks", page_section: "browse parks", page_type: "parks listing" },
  "/how-it-works": { page_name: "how it works", page_section: "how it works", page_type: "how it works:information" },
  "/ferries": { page_name: "ferries", page_section: "ferries", page_type: "ferries:information" },
  "/faq": { page_name: "faqs", page_section: "faqs", page_type: "faqs:information" },
  "/terms": {
    page_name: "terms and conditions",
    page_section: "terms and conditions",
    page_type: "terms and conditions:information",
  },
  "/page/contact-us": { page_name: "contact us", page_section: "contact us", page_type: "contact us:information" },
};

// We need to track two different things with Tealium, Page tracking and Interactions
// This tracks all of the Pages
export const TealiumPageTracker = () => {
  const { pathname } = useLocation();
  const pid = pathname.split("/")[2];

  const [tealiumReady, setTealiumReady] = useState(false);

  useEffect(() => {
    if (window?.utag?.view) {
      setTealiumReady(true);
      return;
    }

    const interval = setInterval(() => {
      if (window.utag?.view) {
        clearInterval(interval);
        setTealiumReady(true);
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);

  const parkData = useSelector(getParkSelector(pid));
  const parkName = parkData ? parkNameWithoutOperator(parkData.PARKNAME, parkData.OPERATOR) : "";

  // Combine static pages with dynamic park page
  const tealiumPages = useMemo(
    () => ({
      ...STATIC_PAGES,
      "/park/:pid": parkData
        ? {
            page_name: `park: ${parkName}`,
            page_section: "park details",
            page_type: "park details",
            article_parent_name: pid,
          }
        : null,
    }),
    [parkData, parkName, pid],
  );

  // This is relatively complex, it should be simple but has many edge cases
  // 1. utag is fetched asynchronously, so we guard against it and wait for it to be on.
  // 2. The name of the park needs to be sent, which means we need to wait until its fetched then recall this function.
  useEffect(() => {
    // Find the first matching route
    const pageMeta =
      Object.values(tealiumPages)
        .filter(Boolean)
        .find((_, idx) => {
          const route = Object.keys(tealiumPages)[idx];
          return matchPath(pathname, { path: route, exact: true });
        }) || (pid ? {} : ERROR_PAGE); // fallback to 404 if no pid

    if (!Object.keys(pageMeta).length) return; // skip if pageMeta is empty
    if (!tealiumReady) return; // skip if tealium isn't ready

    const { page_name, page_section, page_type, ...rest } = pageMeta;

    window.utag.view({
      page_name: page_name || pathname,
      page_section: page_section || "",
      page_type: page_type || "",
      page_restrictions: "public",
      ...rest,
    });
  }, [pathname, tealiumPages, pid, tealiumReady]);

  return null;
};
